import React from "react";
import DOMPurify from "dompurify";
import { ArrowRight, ChevronRight, Phone } from "lucide-react";
import { MGAccordion } from "../../designSystem/components/MGAccordion";
import { MGButton } from "../../designSystem/components/MGButton";
import { MGCard } from "../../designSystem/components/MGCard";
import { MGDocumentList } from "../../designSystem/components/MGDocumentList";
import { MGInputCheckbox } from "../../designSystem/components/MGInputCheckbox";
import { MGLabel } from "../../designSystem/components/MGLabel";
import { MGMicrotext } from "../../designSystem/components/MGMicrotext";
import { MGSubtitle } from "../../designSystem/components/MGSubtitle";
import { MGText } from "../../designSystem/components/MGText";
import { getMarginBetweenComponents } from "./getMarginBetweenComponents";
import styles from "./UmbracoContent.module.css";
import {
  TUmbracoActionItem,
  TUmbracoAccordionItem,
  TUmbracoContentItem,
  TUmbracoContentProps,
  TUmbracoLinkItem,
  TUmbracoMediaAction,
  TUmbracoTypographyItem,
} from "./types";

const formatFileSize = (bytes?: number) => {
  if (!bytes || bytes <= 0) return undefined;

  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value >= 10 || unitIndex === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`;
};

const SafeHtml = ({ className, html }: { className?: string; html: string }) => (
  <div
    className={className}
    dangerouslySetInnerHTML={{
      __html: DOMPurify.sanitize(html, { USE_PROFILES: { html: true } }),
    }}
  />
);

const openExternalUrl = (url?: string) => {
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
};

const renderTypography = (item: TUmbracoTypographyItem, dataTestId?: string) => {
  const className = `${styles.richText} ${styles[item.type]}`;

  return (
    <div key={item.id} data-testid={dataTestId && `${dataTestId}-${item.type}-${item.id}`}>
      <SafeHtml className={className} html={item.text} />
    </div>
  );
};

export const UmbracoContent = ({
  className,
  data,
  dataTestId,
  onCheckboxChange,
  onDocumentClick,
  onMediaClick,
  onNavigate,
  selectedCheckboxIds = [],
  readDocumentIds = [],
  documentAppearance,
}: TUmbracoContentProps) => {
  const handleAction = (item: TUmbracoActionItem | TUmbracoLinkItem) => {
    if (item.linkType === "Content") {
      const destination = item.path || item.url;
      if (!destination) return;
      if (onNavigate) onNavigate(destination);
      else window.location.assign(destination);
      return;
    }

    if (item.linkType === "Media" && item.url) {
      const media: TUmbracoMediaAction = {
        id: item.id,
        title: item.linkTitle || item.title,
        url: item.url,
      };
      if (onMediaClick) onMediaClick(media);
      else openExternalUrl(item.url);
      return;
    }

    openExternalUrl(item.url);
  };

  const renderDocumentList = (item: Extract<TUmbracoContentItem, { type: "documentList" }>) => (
    <MGDocumentList
      appearance={documentAppearance}
      key={item.id}
      title={item.title}
      dataTestId={dataTestId && `${dataTestId}-documents-${item.id}`}
      data={item.documents.map((document) => ({
        id: document.id,
        name: document.name,
        extension: document.extension || "pdf",
        additionalInfo: {
          size: formatFileSize(document.bytes),
        },
        checked: readDocumentIds.includes(document.id),
        onDownloadClick: () => {
          if (onDocumentClick) onDocumentClick(document);
          else openExternalUrl(document.url);
        },
      }))}
    />
  );

  const renderAccordionItems = (items: TUmbracoAccordionItem[]) =>
    items.map((accordionItem) => ({
      id: accordionItem.id,
      title: accordionItem.title,
      dataTestId: dataTestId && `${dataTestId}-accordion-${accordionItem.id}`,
      children: (
        <UmbracoContent
          data={accordionItem.content}
          dataTestId={dataTestId && `${dataTestId}-accordion-content-${accordionItem.id}`}
          onCheckboxChange={onCheckboxChange}
          onDocumentClick={onDocumentClick}
          onMediaClick={onMediaClick}
          onNavigate={onNavigate}
          selectedCheckboxIds={selectedCheckboxIds}
        />
      ),
    }));

  const renderItem = (item: TUmbracoContentItem) => {
    switch (item.type) {
      case "title":
      case "subtitle":
      case "highlightedText":
      case "text":
      case "smallText":
      case "dateText":
      case "checkboxText":
        return renderTypography(item, dataTestId);

      case "largeButton":
      case "smallButton":
        return (
          <MGButton
            key={item.id}
            size={item.type === "smallButton" ? "sm" : "lg"}
            fullWidth={item.type === "largeButton"}
            dataTestId={dataTestId && `${dataTestId}-${item.type}-${item.id}`}
            onClick={() => handleAction(item)}>
            {item.title}
          </MGButton>
        );

      case "image": {
        const aspectRatio =
          item.width && item.height ? `${item.width} / ${item.height}` : undefined;
        return (
          <img
            key={item.id}
            className={styles.image}
            src={item.url}
            alt={item.name}
            style={{ aspectRatio }}
            data-testid={dataTestId && `${dataTestId}-image-${item.id}`}
          />
        );
      }

      case "bulletList":
        return (
          <ul
            key={item.id}
            className={styles.bulletList}
            data-testid={dataTestId && `${dataTestId}-bullets-${item.id}`}>
            {item.bullets.map((bullet) => (
              <li key={bullet.id} className={styles.bulletItem}>
                <ChevronRight className={styles.bulletIcon} size={20} aria-hidden="true" />
                <div className={styles.bulletContent}>
                  {bullet.title && (
                    <MGLabel tagName="div" labelStyleDefinition="80-06">
                      {bullet.title}
                    </MGLabel>
                  )}
                  {bullet.text && <SafeHtml className={styles.richText} html={bullet.text} />}
                </div>
              </li>
            ))}
          </ul>
        );

      case "documentList":
        return renderDocumentList(item);

      case "leftIconLink":
      case "rightIconLink":
        return (
          <button
            key={item.id}
            type="button"
            className={styles.link}
            onClick={() => handleAction(item)}
            data-testid={dataTestId && `${dataTestId}-${item.type}-${item.id}`}>
            {item.type === "leftIconLink" && <ChevronRight size={20} aria-hidden="true" />}
            <MGText
              tagName="span"
              textStyleDefinition="body-100-05"
              color="text-link-enabled-bg-light"
              className={styles.linkText}>
              {item.title}
            </MGText>
            {item.type === "rightIconLink" && <ArrowRight size={20} aria-hidden="true" />}
          </button>
        );

      case "checkboxList":
        return (
          <div
            key={item.id}
            className={styles.checkboxList}
            data-testid={dataTestId && `${dataTestId}-checkboxes-${item.id}`}>
            {item.checkboxes.map((checkbox) => {
              const checkboxId = `umbraco-checkbox-${item.id}-${checkbox.id}`;
              const checked = selectedCheckboxIds.includes(checkbox.id);
              return (
                <div key={checkbox.id} className={styles.checkboxItem}>
                  <MGInputCheckbox
                    id={checkboxId}
                    checked={checked}
                    onChange={(event) => onCheckboxChange?.(checkbox.id, event.target.checked)}
                  />
                  <MGText
                    tagName="label"
                    htmlFor={checkboxId}
                    textStyleDefinition="body-100-03"
                    className={styles.checkboxLabel}>
                    <SafeHtml html={checkbox.description} />
                  </MGText>
                </div>
              );
            })}
          </div>
        );

      case "contactCard":
        return (
          <MGCard
            key={item.id}
            backgroundColor="white"
            borderRadius="xxxs"
            padding="xxxs"
            shadow="20"
            dataTestId={dataTestId && `${dataTestId}-contact-${item.id}`}>
            <address className={styles.contactCard}>
              <MGSubtitle
                tagName="div"
                subtitleStyleDefinition="100-06"
                className={styles.contactTitle}>
                {item.title}
              </MGSubtitle>
              <a
                className={styles.contactLink}
                href={`tel:${item.contact.replace(/\s/g, "")}`}
                aria-label={`Telefone ${item.contact}`}>
                <Phone size={20} aria-hidden="true" />
                <MGText tagName="span" textStyleDefinition="body-300-06" color="text-secondary-01">
                  {item.contact}
                </MGText>
              </a>
              {item.description && (
                <MGMicrotext
                  tagName="div"
                  microtextStyleDefinition="microcopy-80-03"
                  color="text-primary-02"
                  className={styles.contactDescription}>
                  <SafeHtml html={item.description} />
                </MGMicrotext>
              )}
            </address>
          </MGCard>
        );

      case "accordion":
        return (
          <MGAccordion
            key={item.id}
            items={renderAccordionItems([item])}
            dataTestId={dataTestId && `${dataTestId}-accordion-group-${item.id}`}
          />
        );

      case "accordionList":
        return (
          <MGAccordion
            key={item.id}
            title={item.title}
            items={renderAccordionItems(item.items)}
            dataTestId={dataTestId && `${dataTestId}-accordion-list-${item.id}`}
          />
        );
    }
  };

  if (!data.length) return null;

  return (
    <div
      className={`${styles.content}${className ? ` ${className}` : ""}`}
      data-testid={dataTestId}>
      {data.map((item, index) => (
        <div
          key={item.id}
          className={styles.contentItem}
          style={{ marginBottom: getMarginBetweenComponents(item, data[index + 1]) }}>
          {renderItem(item)}
        </div>
      ))}
    </div>
  );
};
